import Image from 'next/image';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@savvy-kit/components/card';
import { cn } from '@savvy-kit/lib/utils';
import { CircleAlert, CircleCheck } from 'lucide-react';
import TrackedButton from 'src/components/tracked/tracked-button';

export type ConnectAccountsNudgeCardStatus = 'default' | 'loading' | 'success' | 'error';

type ConnectAccountsNudgeCardProps = {
  readonly status?: ConnectAccountsNudgeCardStatus;
  readonly description?: string;
  readonly connectLabel?: string;
  readonly className?: string;
  readonly onConnect: () => void;
  readonly onDismiss: () => void;
};

const DEFAULT_DESCRIPTION = 'See your full financial picture by linking accounts you hold outside of Savvy.';

const DEFAULT_CONNECT_LABEL = 'Add assets';

const TERMINAL_COPY: Record<'success' | 'error', { readonly title: string; readonly body: string }> = {
  success: {
    title: 'Account connected!',
    body: 'Your outside account is now part of your net worth.',
  },
  error: {
    title: "Connection didn't finish",
    body: 'Looks like the connection window closed early. No worries — you can pick back up anytime.',
  },
};

function ConnectAccountsNudgeCard({
  status = 'default',
  description = DEFAULT_DESCRIPTION,
  connectLabel = DEFAULT_CONNECT_LABEL,
  className,
  onConnect,
  onDismiss,
}: ConnectAccountsNudgeCardProps) {
  const isTerminal = status === 'success' || status === 'error';

  return (
    <Card
      className={cn('justify-center gap-4 p-0', className)}
      data-status={status}
      data-testid="connect-accounts-nudge-card"
    >
      {!isTerminal && (
        <Image
          src="/net-worth/connect-accounts-illustration.svg"
          alt=""
          width={446}
          height={226}
          className="min-h-[160px] w-full flex-1 px-6 pt-4 object-contain"
          priority
        />
      )}

      {isTerminal && (
        <div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary mx-6 mt-6">
          {status === 'success' && <CircleCheck className="size-6" />}
          {status === 'error' && <CircleAlert className="size-6" />}
        </div>
      )}

      <CardHeader className="px-6" aria-live="polite" aria-atomic="true">
        <CardTitle className="text-lg font-semibold">
          {isTerminal ? TERMINAL_COPY[status].title : 'Connect your accounts'}
        </CardTitle>
        <CardDescription>{isTerminal ? TERMINAL_COPY[status].body : description}</CardDescription>
      </CardHeader>

      {!isTerminal && (
        <CardContent className="flex flex-col gap-2 px-6 pb-6">
          <TrackedButton
            elementName="connect-accounts-nudge-card-connect-button"
            variant="secondary"
            isLoading={status === 'loading'}
            onClick={onConnect}
          >
            {connectLabel}
          </TrackedButton>
          <TrackedButton elementName="connect-accounts-nudge-card-dismiss-button" variant="ghost" onClick={onDismiss}>
            Maybe later
          </TrackedButton>
        </CardContent>
      )}

      {status === 'error' && (
        <CardContent className="flex flex-col gap-2 px-6 pb-6">
          <TrackedButton
            elementName="connect-accounts-nudge-card-try-again-button"
            variant="secondary"
            onClick={onConnect}
          >
            Try again
          </TrackedButton>
          <TrackedButton elementName="connect-accounts-nudge-card-dismiss-button" variant="ghost" onClick={onDismiss}>
            Maybe later
          </TrackedButton>
        </CardContent>
      )}
    </Card>
  );
}

export default ConnectAccountsNudgeCard;
